import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {BehaviorSubject} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {helpKeyForRoutePath} from '@shared/services/contextual-help/page-help-keys';

/**
 * Resolves the contextual help identifier for the page the user is currently on, so a single
 * help affordance in the topbar can serve every screen.
 *
 * The identifier comes from the route *configuration* path rather than the URL, so it is unaffected
 * by route parameter values, query strings and fragments.
 */
@Injectable({providedIn: 'root'})
export class PageHelpService {
  private readonly helpKeySubject = new BehaviorSubject<string | null>(null);

  /** Current page's help identifier, or null when the page has no help mapping. */
  readonly helpKey$: Observable<string | null> = this.helpKeySubject.asObservable();

  constructor(private router: Router) {
    this.helpKeySubject.next(this.resolveHelpKey());

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.resolveHelpKey())
      )
      .subscribe(helpKey => {
        if (helpKey !== this.helpKeySubject.value) {
          this.helpKeySubject.next(helpKey);
        }
      });
  }

  get helpKey(): string | null {
    return this.helpKeySubject.value;
  }

  /**
   * Help identifier for the currently activated route, or null when unmapped.
   *
   * The concrete path (route parameters replaced by their values) is tried first, so a route that
   * serves a different page per parameter -- `report/ReportNavigator/:module` renders the payroll
   * or the configuration report list -- can map each one separately. The declared path is the
   * fallback, so ordinary pages need only one entry.
   */
  resolveHelpKey(): string | null {
    return helpKeyForRoutePath(this.currentRoutePath(true))
      || helpKeyForRoutePath(this.currentRoutePath(false));
  }

  /**
   * Joins the `path` of every route configuration from the root of the activated tree down to the
   * leaf, e.g. `employee/EmployeeViewEdit/:id/:sortdirec`. Empty paths (layout and lazy-module
   * wrappers) contribute nothing.
   */
  private currentRoutePath(concrete: boolean): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot = this.router.routerState
      && this.router.routerState.snapshot
      && this.router.routerState.snapshot.root;

    while (route) {
      const path = route.routeConfig && route.routeConfig.path;
      if (path) {
        segments.push(concrete ? this.substituteParams(path, route) : path);
      }
      route = route.firstChild;
    }

    return segments.join('/');
  }

  /** Replaces `:param` segments with the values the router resolved for them. */
  private substituteParams(path: string, route: ActivatedRouteSnapshot): string {
    if (path.indexOf(':') === -1) {
      return path;
    }

    const params = route.params || {};
    return path
      .split('/')
      .map(segment => {
        if (segment.charAt(0) !== ':') {
          return segment;
        }
        const value = params[segment.substring(1)];
        return value === undefined || value === null ? segment : String(value);
      })
      .join('/');
  }
}
